<script setup lang="ts">
import moment from "moment";
import { computed } from "vue";

import DatePicker from "~/components/dumb/DatePicker.vue";
import { ReminderIcon } from "~/icons";
import { CommandId, EditorMode, Placement, ViewKind } from "~/shared/enums";
import type { Task } from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { getFullDate, getRelativeTimeForReminder } from "~/utils/time";

import Tooltip from "./Tooltip.vue";

const props = defineProps<{
  editorMode: EditorMode;
  task: Task;
  remindAt: string | null;
  containerClasses?: string;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  onClickFinishedIndicator?: (event: KeyboardEvent | MouseEvent) => void;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const isBoardMode = computed(() => props.editorMode === EditorMode.BOARD);
const isDetailMode = computed(() => props.editorMode === EditorMode.DETAIL);

const isTrash = computed(() => appStore.currentPage?.kind === ViewKind.TRASH);

const isPastReminder = computed(() => moment(props.remindAt).isSameOrBefore(moment()));

const reminderTime = computed(() => {
  const time = getRelativeTimeForReminder(moment(props.remindAt)).value;
  return isBoardMode.value || isDetailMode.value ? time.replace(/^./, (e) => e.toUpperCase()) : time;
});
const reminderDateTime = computed(() => getFullDate(moment(props.remindAt)));
</script>

<template>
  <DatePicker
    :value="task.remindAt"
    :editor-mode="editorMode"
    :tasks="[task]"
    :remind-at="task.remindAt"
    is-reminder
    cover
    :class="containerClasses"
    :on-after-open="onAfterOpen"
    :on-after-close="onAfterClose"
    :disabled="isBoardMode || pageStore.isPublicView || isTrash">
    <Tooltip :placement="Placement.TOP" :text="reminderDateTime" class="w-full">
      <Tooltip
        :command-id="isBoardMode || isPastReminder ? undefined : CommandId.SET_REMINDER"
        :text="isPastReminder ? 'Clear past reminder' : isBoardMode ? 'Reminder' : undefined"
        class="w-full">
        <!-- Reminder expired indicator -->
        <button
          v-if="remindAt && moment(remindAt).isSameOrBefore(moment())"
          type="button"
          class="flex icon-md items-center justify-center rounded-full focus-ring-none"
          tabindex="-1"
          @click="onClickFinishedIndicator"
          @keydown.enter="onClickFinishedIndicator">
          <div class="size-2 rounded-full bg-primary-base" />
          <span class="sr-only">Reminder expired indicator</span>
        </button>
        <!-- Reminder countdown -->
        <button
          v-else-if="remindAt && moment(remindAt).isAfter(moment())"
          type="button"
          class="flex items-center rounded-sm focus-ring-none"
          :class="[
            isBoardMode && 'text-md',
            isDetailMode
              ? 'w-full justify-start gap-2 px-2 py-1 text-md hover:bg-lt'
              : 'h-5 justify-center gap-1 border border-oncolor px-1 text-vlt hover:bg-opposite/10',
          ]"
          tabindex="-1">
          <span class="sr-only">Reminder countdown</span>
          <ReminderIcon class="icon-xs" />
          <span class="text-md" :class="isDetailMode ? 'text-left text-sm' : 'text-xs'">
            {{ reminderTime }}
          </span>
        </button>
      </Tooltip>
    </Tooltip>
  </DatePicker>
</template>
